import type { AuthKitRequest, AuthKitStep, DevicePasskey, MockAuthKit } from "../demo";
import {
  CodeRejectedError,
  CodeThrottledError,
  SignInExpiredError,
  ValidationError,
} from "../errors";
import {
  checkCode,
  isEmail,
  issueCode,
  normaliseEmail,
  pruneSends,
  throttleCodes,
} from "../logic/codes";
import { invitationStatus } from "../logic/invitations";
import { randomDigits, sha256 } from "../logic/tokens";
import { IDENTITY_POLICY } from "../policy";
import type { AuthMethod } from "../types";
import { describeDevice, findOrg, initialsOf, userByEmail, type Ctx } from "./context";

/**
 * Stand-in for the WorkOS AuthKit hosted page: Magic Auth email codes, passkeys and Google or Microsoft, all
 * simulated. Sign-up is disabled unless an invitation re-opened it. Finishing hands the browser a single-use code
 * for /auth/callback, as AuthKit does.
 */

const NAME_MAX = 80;
const COLOURS = 6;

export function createMockAuthKit(ctx: Ctx): MockAuthKit {
  const policy = IDENTITY_POLICY.code;

  const pending = (state: string) => {
    const signIn = ctx.state.signIns.find((s) => s.state === state && s.expiresAt > ctx.now());
    if (signIn === undefined) throw new SignInExpiredError();
    const invitation = ctx.state.invitations.find((inv) => inv.id === signIn.invitationId);
    const usable =
      invitation !== undefined && invitationStatus(invitation, ctx.now()) === "pending"
        ? invitation
        : undefined;
    return { signIn, invitation: usable };
  };

  const codeKey = (state: string, email: string, code: string) =>
    sha256(`${state}:${email}:${code}`);

  /** Finish in AuthKit: maybe offer a passkey, then issue the callback code. */
  const authenticate = async (
    state: string,
    rawEmail: string,
    method: AuthMethod,
    name?: string,
  ): Promise<AuthKitStep> => {
    const email = normaliseEmail(rawEmail);
    const { invitation } = pending(state);
    let user = userByEmail(ctx, email);
    if (user === undefined) {
      // AuthKit sign-up is disabled; only an invitation for this exact email re-opens it (FR-ADMIN-001, 052).
      if (invitation === undefined || invitation.email !== email)
        return { kind: "noAccount", email };
      const cleanName = (name ?? "").trim();
      if (cleanName === "") throw new ValidationError("name", "Enter your name.");
      if (cleanName.length > NAME_MAX)
        throw new ValidationError("name", `Use ${String(NAME_MAX)} characters or fewer.`);
      user = {
        id: `p-${ctx.token().slice(0, 10)}`,
        name: cleanName,
        email,
        initials: initialsOf(cleanName),
        colour: (ctx.state.users.length % COLOURS) + 1,
        title: "",
        createdAt: ctx.now(),
        workspaceChosen: false,
        timeZone: "Asia/Kolkata",
        units: "metric",
        passkeys: [],
        passkeyPromptDismissed: false,
      };
      ctx.state.users.push(user);
    }
    ctx.state.authenticated = ctx.state.authenticated.filter((a) => a.state !== state);
    ctx.state.authenticated.push({
      state,
      userId: user.id,
      method,
      expiresAt: ctx.now() + IDENTITY_POLICY.signInRequestMs,
    });
    ctx.persist();
    // AuthKit's progressive enrolment: after an email code, people without a passkey are offered one.
    if (method === "email_code" && user.passkeys.length === 0 && !user.passkeyPromptDismissed)
      return { kind: "passkeyPrompt", email };
    return issueCallback(state);
  };

  const issueCallback = async (state: string): Promise<AuthKitStep> => {
    const done = ctx.state.authenticated.find((a) => a.state === state);
    if (done === undefined) throw new SignInExpiredError();
    const code = ctx.token();
    done.callbackCodeHash = await sha256(code);
    done.expiresAt = ctx.now() + IDENTITY_POLICY.callbackCodeMs;
    ctx.persist();
    return {
      kind: "redirect",
      url: `/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    };
  };

  return {
    async describe(state): Promise<AuthKitRequest> {
      await ctx.wait();
      const { signIn, invitation } = pending(state);
      const org = invitation === undefined ? undefined : findOrg(ctx, invitation.organisationId);
      return {
        state,
        screen: signIn.screen,
        invitation:
          invitation === undefined || org === undefined
            ? undefined
            : {
                email: invitation.email,
                organisationName: org.name,
                inviterName: invitation.invitedBy,
                role: invitation.role,
              },
      };
    },

    async sendCode(state, rawEmail) {
      await ctx.wait();
      const email = normaliseEmail(rawEmail);
      if (!isEmail(email))
        throw new ValidationError("email", "Enter an email address like name@firm.in.");
      const { invitation } = pending(state);
      if (invitation !== undefined && invitation.email !== email) {
        throw new ValidationError(
          "email",
          `Use ${invitation.email}, the address this invitation was sent to.`,
        );
      }
      const now = ctx.now();
      const sends = pruneSends(ctx.state.codeSends[email] ?? [], now, policy);
      const throttle = throttleCodes(sends, now, policy);
      if (!throttle.allowed) throw new CodeThrottledError(new Date(throttle.retryAt).toISOString());
      // Identical for known and unknown emails: nothing here reveals whether an account exists (FR-ADMIN-003).
      const code = randomDigits(ctx.options.random, policy.digits);
      const issued = issueCode(now, policy);
      ctx.state.magicCodes = ctx.state.magicCodes.filter(
        (c) => !(c.state === state && c.email === email),
      );
      ctx.state.magicCodes.push({
        state,
        email,
        codeHash: await codeKey(state, email, code),
        ...issued,
        lockedUntil: undefined,
      });
      ctx.state.codeSends[email] = [...sends, now];
      ctx.persist();
      return {
        demoCode: code,
        expiresAt: new Date(issued.expiresAt).toISOString(),
        resendAvailableAt: new Date(now + policy.resendAfterMs).toISOString(),
      };
    },

    async verifyCode(state, rawEmail, code, name) {
      await ctx.wait();
      const email = normaliseEmail(rawEmail);
      pending(state);
      const record = ctx.state.magicCodes.find((c) => c.state === state && c.email === email);
      if (record === undefined) throw new CodeRejectedError("expired");
      const matches = (await codeKey(state, email, code.trim())) === record.codeHash;
      const checked = checkCode(
        { ...record, lockedUntil: record.lockedUntil },
        matches,
        ctx.now(),
        policy,
      );
      Object.assign(record, { attempts: checked.code.attempts, used: checked.code.used });
      if (checked.code.lockedUntil !== undefined) record.lockedUntil = checked.code.lockedUntil;
      ctx.persist();
      if (checked.result === "wrong") throw new CodeRejectedError("wrong", checked.attemptsLeft);
      if (checked.result === "locked")
        throw new CodeRejectedError("locked", 0, new Date(checked.until).toISOString());
      if (checked.result === "expired") throw new CodeRejectedError("expired");
      return authenticate(state, email, "email_code", name);
    },

    async devicePasskeys(): Promise<DevicePasskey[]> {
      await ctx.wait();
      return ctx.state.users.flatMap((user) =>
        user.passkeys.map((passkey) => ({
          credentialId: passkey.id,
          email: user.email,
          label: passkey.label,
        })),
      );
    },

    async signInWithPasskey(state, credentialId) {
      await ctx.wait();
      pending(state);
      const user = ctx.state.users.find((u) => u.passkeys.some((p) => p.id === credentialId));
      if (user === undefined)
        throw new ValidationError("passkey", "That passkey isn't registered with Orthos any more.");
      return authenticate(state, user.email, "passkey");
    },

    async signInWithProvider(state, provider, rawEmail, name) {
      await ctx.wait();
      const email = normaliseEmail(rawEmail);
      if (!isEmail(email))
        throw new ValidationError("email", "Enter the email of the account you're using.");
      pending(state);
      // FR-ADMIN-004: Google with email_verified, Microsoft with a verified email claim; the simulation treats both as verified.
      return authenticate(state, email, provider, name);
    },

    async finishPasskeyPrompt(state, addPasskey) {
      await ctx.wait();
      pending(state);
      const done = ctx.state.authenticated.find((a) => a.state === state);
      const user =
        done === undefined ? undefined : ctx.state.users.find((u) => u.id === done.userId);
      if (user === undefined) throw new SignInExpiredError();
      if (addPasskey) {
        const { device, browser } = describeDevice(ctx.options.userAgent);
        user.passkeys.push({
          id: `pk-${ctx.token()}`,
          label: `${browser} on ${device}`,
          createdAt: ctx.now(),
        });
      } else {
        user.passkeyPromptDismissed = true;
      }
      ctx.persist();
      return issueCallback(state);
    },
  };
}
